// Pure maths for the fixed-fee client statement (CPBM). No Firestore/React.
//
// The client pays a fixed fee every month plus tax, but deducts withholding
// when it pays: income-tax WHT on the whole invoice and part of the sales tax.
// Out of the cash that arrives ALM pays the month's salaries (actual working
// days), expenses, taxes and a fixed service charge; what is left is saved.
//   invoiced  = fee + sales tax charged
//   withheld  = WHT (rate x invoiced) + sales tax withheld (rate x sales tax),
//               each truncated to whole rupees as the client pays it
//   cash in   = invoiced - withheld          (verified against CPBM's Sept-2026 payment)
//   paid out  = salaries + expenses + taxes ALM actually paid + service charge
//   saved     = cash in - paid out
// Everything is on a cash basis: the sales tax the client withholds is already
// missing from "cash in" (the client deposits it with PRA itself), so the
// "Taxes paid" lines must be what ALM really paid PRA — never the full tax.

export const FIXED_FEE_CLIENTS = Object.freeze([
  { code: "CPBM", name: "Century Board and Paper Mill", defaultServiceCharge: 80000, defaultTaxRate: 0.16, defaultWhtRate: 0.07, defaultTaxWithholdingRate: 0.2,
    // CPBM deducted 6% income-tax WHT up to June 2026 and 7% from July 2026 (by invoice month).
    whtRateSchedule: [{ from: "0000-00", rate: 0.06 }, { from: "2026-07", rate: 0.07 }],
    // CPBM began withholding 20% of the sales tax with the September 2026 payment; earlier months had none.
    taxWithholdingSchedule: [{ from: "0000-00", rate: 0 }, { from: "2026-09", rate: 0.2 }] }
]);

export const SECTIONS = Object.freeze({
  SALARY: "Salaries (actual working days)",
  EXPENSE: "Expenses",
  TAX: "Taxes paid"
});

export const round = (value) => Math.round(Number(value || 0));

export const clientOf = (code) => FIXED_FEE_CLIENTS.find((client) => client.code === code) || FIXED_FEE_CLIENTS[0];

export function taxWithholdingRateFor(clientCode, period) {
  const client = clientOf(clientCode);
  let rate = client.defaultTaxWithholdingRate;
  for (const step of client.taxWithholdingSchedule || []) {
    if (String(period) >= step.from) rate = step.rate;
  }
  return rate;
}

export function whtRateFor(clientCode, period) {
  const client = clientOf(clientCode);
  let rate = client.defaultWhtRate;
  for (const step of client.whtRateSchedule || []) {
    if (String(period) >= step.from) rate = step.rate;
  }
  return rate;
}

export function emptyStatement(clientCode, period) {
  const client = clientOf(clientCode);
  return {
    clientCode: client.code, period, fee: "", taxRate: client.defaultTaxRate, taxCollected: null,
    whtRate: whtRateFor(client.code, period), whtWithheld: null, taxWithholdingRate: taxWithholdingRateFor(client.code, period), taxWithheld: null,
    serviceCharge: client.defaultServiceCharge, lines: [], notes: ""
  };
}

export const defaultTaxCollected = (fee, taxRate) => round(Number(fee || 0) * Number(taxRate ?? 0));
const blank = (value) => value == null || value === "";
const floorTo = (value) => Math.floor(Number(value || 0) + 1e-9);

export function statementTotals(statement) {
  const lines = statement.lines || [];
  const section = (name) => round(lines.filter((line) => line.section === name).reduce((sum, line) => sum + Number(line.amount || 0), 0));
  const fee = round(statement.fee);
  const taxCollected = blank(statement.taxCollected) ? defaultTaxCollected(fee, statement.taxRate) : round(statement.taxCollected);
  const invoiced = fee + taxCollected;
  const whtWithheld = blank(statement.whtWithheld) ? floorTo(invoiced * Number(statement.whtRate ?? 0)) : round(statement.whtWithheld);
  const taxWithheld = blank(statement.taxWithheld) ? floorTo(taxCollected * Number(statement.taxWithholdingRate ?? 0)) : round(statement.taxWithheld);
  const withheldTotal = whtWithheld + taxWithheld;
  const received = invoiced - withheldTotal;
  const salaries = section("SALARY");
  const expenses = section("EXPENSE");
  const taxesPaid = section("TAX");
  const serviceCharge = round(statement.serviceCharge);
  const paidOut = salaries + expenses + taxesPaid + serviceCharge;
  const saved = received - paidOut;
  return {
    fee, taxCollected, invoiced, whtWithheld, taxWithheld, withheldTotal, received, salaries, expenses, taxesPaid, serviceCharge, paidOut, saved,
    savedPercentOfFee: fee > 0 ? Math.round((saved / fee) * 1000) / 10 : 0
  };
}

// Statements oldest-first with a running total of what has been saved.
export function withCumulative(statements) {
  let running = 0;
  return [...statements]
    .sort((a, b) => String(a.period).localeCompare(String(b.period)))
    .map((statement) => {
      const totals = statementTotals(statement);
      running += totals.saved;
      return { ...statement, totals, cumulativeSaved: running };
    });
}

export function grandTotals(statements) {
  const rows = withCumulative(statements);
  const sum = (key) => rows.reduce((total, row) => total + row.totals[key], 0);
  return {
    months: rows.length, fee: sum("fee"), taxCollected: sum("taxCollected"), invoiced: sum("invoiced"), whtWithheld: sum("whtWithheld"),
    taxWithheld: sum("taxWithheld"), withheldTotal: sum("withheldTotal"), received: sum("received"), salaries: sum("salaries"),
    expenses: sum("expenses"), taxesPaid: sum("taxesPaid"), serviceCharge: sum("serviceCharge"), paidOut: sum("paidOut"), saved: sum("saved")
  };
}

export function validateStatement(statement) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(String(statement.period || ""))) return "Choose the month.";
  if (!(round(statement.fee) > 0)) return "Enter the fixed fee for the month.";
  for (const line of statement.lines || []) {
    if (!SECTIONS[line.section]) return "A line has no section.";
    if (!String(line.description || "").trim()) return "Every line needs a description.";
    if (!(Number(line.amount) > 0)) return `"${line.description}" needs an amount greater than zero.`;
  }
  return "";
}

// Cleans a statement for saving: numbers as numbers, empty lines dropped.
export function normalizeStatement(statement) {
  return {
    clientCode: statement.clientCode, period: statement.period,
    fee: round(statement.fee), taxRate: Number(statement.taxRate ?? 0),
    taxCollected: blank(statement.taxCollected) ? null : round(statement.taxCollected),
    whtRate: Number(statement.whtRate ?? 0), whtWithheld: blank(statement.whtWithheld) ? null : round(statement.whtWithheld),
    taxWithholdingRate: Number(statement.taxWithholdingRate ?? 0), taxWithheld: blank(statement.taxWithheld) ? null : round(statement.taxWithheld),
    serviceCharge: round(statement.serviceCharge),
    lines: (statement.lines || [])
      .filter((line) => String(line.description || "").trim() || Number(line.amount))
      .map((line) => ({ section: line.section, description: String(line.description || "").trim(), amount: round(line.amount), note: String(line.note || "").trim() })),
    notes: String(statement.notes || "").trim()
  };
}
